import { useState } from "react";
import Box from "@mui/material/Box";
import { BarcodeIcon } from "../../shared/components/icons";
import { requests } from "../../shared/api";
import { MIN_ICON_TARGET_FINE, MIN_TARGET } from "../../theme";

/**
 * The pressable icon's hit area, exactly the theme's POINTER_BOX contract:
 * 36px on a fine pointer and 44px on a coarse one. The image keeps its own
 * visual size inside the wrapper, so a 40px thumbnail stays 40px visually
 * while still carrying the 44px touch target (spec §10 amendment).
 */
export function thumbnailTargetBox() {
  return {
    minWidth: MIN_ICON_TARGET_FINE,
    minHeight: MIN_ICON_TARGET_FINE,
    "@media (pointer: coarse)": {
      minWidth: MIN_TARGET,
      minHeight: MIN_TARGET,
    },
  } as const;
}

/** The item surface an icon needs — anything carrying the id, name, legacy
 *  external photo URL, and ordered photo list. The first photo is the cover. */
export interface ItemThumbnailSource {
  id: string;
  name: string;
  photo: string | null;
  photos: Array<{ id: string }>;
}

/** Item row icon for lists: first-photo preview → legacy external photo →
 *  neutral placeholder. Falls forward on image load errors, and always renders
 *  a box of the given size so rows align. An optional `onClick` makes the icon
 *  a small pressable button (used for item navigation). */
export function ItemThumbnail({
  item,
  size = 40,
  onClick,
}: {
  item: ItemThumbnailSource;
  size?: number;
  onClick?: () => void;
}) {
  const previewUrl = item.photos[0]
    ? requests.media.itemPhotoPreviewUrl(item.id, item.photos[0].id)
    : undefined;
  const externalUrl = item.photo ?? undefined;
  const urls: Array<string | undefined> = [previewUrl, externalUrl, undefined];
  const [step, setStep] = useState(() => (previewUrl ? 0 : externalUrl ? 1 : 2));
  const src = urls[Math.min(step, urls.length - 1)];

  const inner = src ? (
    <Box
      component="img"
      src={src}
      alt={item.name}
      onError={() => setStep((s) => s + 1)}
      sx={{ width: size, height: size, borderRadius: 1, objectFit: "cover", flexShrink: 0, display: "block" }}
    />
  ) : (
    <Box
      aria-label={item.name}
      role="img"
      sx={{
        width: size,
        height: size,
        borderRadius: 1,
        bgcolor: "grey.200",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <BarcodeIcon fontSize="small" color="disabled" />
    </Box>
  );

  if (!onClick) return inner;

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-label={item.name}
      sx={{
        padding: 0,
        border: "none",
        background: "transparent",
        borderRadius: 1,
        cursor: "pointer",
        lineHeight: 0,
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        ...thumbnailTargetBox(),
      }}
    >
      {inner}
    </Box>
  );
}